import React from 'react';
import { useTranslation } from 'react-i18next';

import { 
    Modal, 
    Button, 
    TextField, 
    TextareaField, 
    SelectField,
    TabbedComponent,
    Badge,
    IconButton
} from '@dopomogai/ui';
import type { ProjectTask, TaskLink } from '@dopomogai/types';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui'; // Adjust path
import { Star, Link2, Plus, Trash2 } from 'lucide-react';

interface TaskEditorModalProps {
  isOpen: boolean;
  taskData: ProjectTask | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (task: ProjectTask) => void;
  onDataChange: (data: Partial<ProjectTask>) => void;
}

/**
 * A global modal for creating or editing a Project Task.
 * Uses a tabbed interface to manage multilingual content and other metadata.
 */
export const TaskEditorModal: React.FC<TaskEditorModalProps> = ({
  isOpen,
  taskData,
  isSaving,
  onClose,
  onSave,
  onDataChange,
}) => {
  const { i18n, t } = useTranslation(['admin_tasks', 'common']);

  if (!taskData) return null;

  const isNewTask = !taskData.created_at;
  const modalTitle = isNewTask ? t('editor.newTaskTitle') : t('editor.editTaskTitle');
  const supportedLangs = ['en', 'uk']; // This could come from a config

  const handleSave = () => {
    if (!taskData._id.trim()) {
      alert(t('editor.idRequiredError'));
      return;
    }
    if (!taskData.title.trim()) {
      alert(t('editor.titleRequiredError'));
      return;
    }
    onSave(taskData);
  };
  
  const handleLinkChange = (index: number, field: keyof TaskLink, value: string) => {
    const newLinks = [...(taskData.links || [])];
    newLinks[index] = { ...newLinks[index], [field]: value };
    onDataChange({ links: newLinks });
  };
  
  const addLink = () => {
    const newLinks = [...(taskData.links || []), { text: '', url: '' }];
    onDataChange({ links: newLinks });
  };
  
  const removeLink = (index: number) => {
    const newLinks = (taskData.links || []).filter((_, i) => i !== index);
    onDataChange({ links: newLinks });
  };


  // Define the tabs for the multilingual content
  const contentTabs = supportedLangs.map(lang => ({
      id: lang,
      label: t(`common:lang_${lang}`),
      content: (
          <div className="space-y-4 pt-4">
              <TextField
                  label={t('editor.titleLabel')}
                  value={taskData.title} // Assuming default lang is always present
                  onChange={(e) => onDataChange({ title: e.target.value })}
                  placeholder={t('editor.titlePlaceholder')}
              />
              <TextareaField
                  label={t('editor.descriptionLabel')}
                  value={taskData.description || ''}
                  onChange={(e) => onDataChange({ description: e.target.value })}
                  rows={5}
              />
          </div>
      )
  }));
  
  // Define the main tabs for the modal
  const mainTabs = [
      {
          id: 'details',
          label: t('editor.tabs.details'),
          content: (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 pt-4">
                <TextField 
                    label={t('editor.idLabel')}
                    value={taskData._id}
                    onChange={(e) => onDataChange({ _id: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder={t('editor.idPlaceholder')}
                    descriptionText={t('editor.idDescription')}
                    disabled={!isNewTask}
                    required
                />
                 <TextField
                    label={t('editor.pointsLabel')}
                    type="number"
                    value={String(taskData.points || '')}
                    onChange={(e) => onDataChange({ points: Number(e.target.value) || undefined })}
                    leadingIcon={<Star size={18} className="text-slate-400" />}
                />
                <SelectField
                    name="task-status"
                    label={t('editor.statusLabel')}
                    value={taskData.status}
                    onChange={val => onDataChange({ status: val as ProjectTask['status'] })}
                    options={[
                        {value: 'todo', label: 'Todo'},
                        {value: 'in-progress', label: 'In Progress'},
                        {value: 'done', label: 'Done'},
                        {value: 'blocked', label: 'Blocked'},
                        {value: 'archived', label: 'Archived'},
                    ]}
                />
                <SelectField
                    name="task-type"
                    label={t('editor.typeLabel')}
                    value={taskData.task_type}
                    onChange={val => onDataChange({ task_type: val as ProjectTask['task_type'] })}
                    options={[
                        {value: 'feature', label: 'Feature'},
                        {value: 'bugfix', label: 'Bugfix'},
                        {value: 'chore', label: 'Chore'},
                        {value: 'docs', label: 'Docs'},
                        {value: 'research', label: 'Research'},
                    ]}
                />
            </div>
          )
      },
      {
          id: 'content',
          label: t('editor.tabs.content'),
          content: <TabbedComponent tabs={contentTabs} />
      },
      {
          id: 'links',
          label: t('editor.tabs.links'),
          content: (
            <div className="pt-4 space-y-3">
              {(taskData.links || []).map((link, index) => (
                  <div key={index} className="flex items-end gap-3 p-3 bg-slate-50 border rounded-lg">
                      <TextField name={`link-text-${index}`} label="Link Text" value={link.text} onChange={(e) => handleLinkChange(index, 'text', e.target.value)} containerClassName="flex-grow" />
                      <TextField name={`link-url-${index}`} label="URL" value={link.url} onChange={(e) => handleLinkChange(index, 'url', e.target.value)} containerClassName="flex-grow" />
                      <IconButton icon={<Trash2 size={16} />} onClick={() => removeLink(index)} variant="danger" />
                  </div>
              ))}
              <Button text="Add Link" variant="secondary" icon={<Plus size={16} />} onClick={addLink} />
            </div>
          )
      },
  ];

  const footer = (
    <div className="flex justify-between items-center w-full">
        <div>
            {taskData.sprint_id && <Badge text={`Sprint: ${taskData.sprint_id}`} accentColor="purple" />}
        </div>
        <div className="flex gap-3">
            <Button textKey="common:cancel" variant="secondary" onClick={onClose} />
            <Button
                textKey={isNewTask ? 'common:createTask' : 'common:saveChanges'}
                onClick={handleSave}
                loading={isSaving}
                data-modal-primary-action="true"
            />
        </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={footer}
      size="3xl" // A very wide modal for this complex editor
    >
      <TabbedComponent tabs={mainTabs} />
    </Modal>
  );
};